import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError } from '@/lib/authUtils';
import { apiRequest } from '@/lib/queryClient';
import { cn } from '@/lib/utils';
import type { UserWithTeam } from '@shared/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: UserWithTeam[];
  teams: any[]; // Team projects (stored as teams in database)
  editTask?: any;
  preselectedAssigneeId?: string;
  currentUser?: any;
  initialDueDate?: string; // YYYY-MM-DD
}

interface TaskRow {
  id: string;
  teamId: string;
  title: string;
  description: string;
  dueDate: string;
  selectedGMId: string;
  selectedPMId: string;
  selectedHODId: string;
  selectedStaffIds: string[];
  selectedUserIds: string[];
  taskType: 'individual' | 'common';
}

export default function TaskModal({
  isOpen,
  onClose,
  users,
  teams,
  editTask,
  preselectedAssigneeId,
  currentUser,
  initialDueDate,
}: TaskModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch cities
  const { data: cities = [] } = useQuery<any[]>({
    queryKey: ['/api/cities'],
    enabled: isOpen && !editTask,
  });

  // Form state for new multi-row task creation
  const [selectedCityId, setSelectedCityId] = useState<string>('');
  const [taskType, setTaskType] = useState<'individual' | 'common'>('common');
  const [selectedGMId, setSelectedGMId] = useState<string>('');
  const [selectedPMId, setSelectedPMId] = useState<string>('');
  const [selectedHODId, setSelectedHODId] = useState<string>('');
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]); // For PM/HOD to assign to multiple staff
  const [addGMAsViewer, setAddGMAsViewer] = useState<boolean>(false);
  const [taskRows, setTaskRows] = useState<TaskRow[]>([]);
  const [showConfirmCloseDialog, setShowConfirmCloseDialog] = useState(false);
  const [gmOpen, setGmOpen] = useState(false);
  const [pmOpen, setPmOpen] = useState(false);
  const [hodOpen, setHodOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [openProjectRowId, setOpenProjectRowId] = useState<string | null>(null);
  const [openGmRowId, setOpenGmRowId] = useState<string | null>(null);
  const [openPmRowId, setOpenPmRowId] = useState<string | null>(null);
  const [openHodRowId, setOpenHodRowId] = useState<string | null>(null);
  const [openDpmRowId, setOpenDpmRowId] = useState<string | null>(null);
  const [openStaffRowId, setOpenStaffRowId] = useState<string | null>(null);

  // Initialize task rows when modal opens (no localStorage)
  useEffect(() => {
    if (isOpen && !editTask) {
      setSelectedCityId('');
      setSelectedGMId('');
      setSelectedPMId('');
      setSelectedHODId('');
      setSelectedStaffIds([]);
      setAddGMAsViewer(false);
      setTaskType('common');
      setTaskRows([
        {
          id: `row-${Date.now()}`,
          teamId: '',
          title: '',
          description: '',
          dueDate: initialDueDate || '',
          selectedGMId: '',
          selectedPMId: '',
          selectedHODId: '',
          selectedStaffIds: [],
          selectedUserIds: [],
          taskType: 'common',
        },
      ]);
    }
  }, [isOpen, editTask, initialDueDate]);

  // Clear form data after successful submission
  const clearSavedData = () => {
    setSelectedCityId('');
    setSelectedGMId('');
    setSelectedPMId('');
    setSelectedHODId('');
    setSelectedStaffIds([]);
    setAddGMAsViewer(false);
    setTaskType('common');
    setTaskRows([
      {
        id: `row-${Date.now()}`,
        teamId: '',
        title: '',
        description: '',
        dueDate: initialDueDate || '',
        selectedGMId: '',
        selectedPMId: '',
        selectedHODId: '',
        selectedStaffIds: [],
        selectedUserIds: [],
        taskType: 'common',
      },
    ]);
  };

  // Determine available assignees based on current user role and hierarchy
  // GM can assign to: PM, HOD
  // PM can assign to: Staff (assigned to them), HOD
  // HOD can assign to: GM, PM, Staff (assigned to them)
  // Admin can assign to: GM, PM, HOD (existing behavior)

  const isGM = currentUser?.role === 'gm';
  const isPM = currentUser?.role === 'pm';
  const isHOD = currentUser?.role === 'hod' || currentUser?.role === 'dpm';
  const isAdmin = currentUser?.role === 'admin';

  const normalizeCityIds = (
    rawCityIds: unknown,
    fallbackCityId?: unknown
  ): string[] => {
    // rawCityIds can be stringified JSON, array, null/undefined
    if (Array.isArray(rawCityIds)) {
      return rawCityIds.filter(Boolean).map(String);
    }
    if (typeof rawCityIds === 'string') {
      try {
        const parsed = JSON.parse(rawCityIds);
        if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
      } catch {
        // ignore parse errors
      }
    }
    if (typeof fallbackCityId === 'string' && fallbackCityId)
      return [fallbackCityId];
    return [];
  };

  // Get current user's city/cities for filtering
  const currentUserCityIds = (() => {
    if (!currentUser) return [];
    const fallbackCityId = (currentUser as any).cityId;
    if (currentUser.role === 'gm') {
      return normalizeCityIds(undefined, fallbackCityId);
    }
    if (
      currentUser.role === 'pm' ||
      currentUser.role === 'hod' ||
      currentUser.role === 'dpm'
    ) {
      return normalizeCityIds((currentUser as any).cityIds, fallbackCityId);
    }
    return normalizeCityIds((currentUser as any).cityIds, fallbackCityId);
  })();

  // For admin: use selected city. For GM/PM/HOD: use selected city if they have multiple, else default to their first city.
  const filterCityId = isAdmin
    ? selectedCityId
    : selectedCityId ||
      currentUserCityIds[0] ||
      (currentUser as any)?.cityId ||
      '';

  const shouldPickCity = isAdmin || (!isAdmin && currentUserCityIds.length > 1);

  // When opening for GM/PM/HOD with a single city, preselect it so filters work
  useEffect(() => {
    if (!isOpen || editTask) return;
    if (isAdmin) return;
    if (currentUserCityIds.length === 1 && !selectedCityId) {
      setSelectedCityId(currentUserCityIds[0] as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editTask, isAdmin, JSON.stringify(currentUserCityIds)]);

  // When non-admin changes city (multi-city PM/HOD), reset selections
  useEffect(() => {
    if (isAdmin) return;
    if (!selectedCityId) return;
    setSelectedGMId('');
    setSelectedPMId('');
    setSelectedHODId('');
    setSelectedStaffIds([]);
  }, [selectedCityId, isAdmin]);

  // Get available GMs, PMs, and HODs based on role
  const availableGMs = (() => {
    if (isHOD && filterCityId) {
      // HOD can assign to GM
      return users.filter(
        u => u.role === 'gm' && (u as any).cityId === filterCityId
      );
    }
    if (isAdmin && selectedCityId) {
      return users.filter(
        u => u.role === 'gm' && (u as any).cityId === selectedCityId
      );
    }
    return [];
  })();

  const availablePMs = (() => {
    if ((isGM || isHOD) && filterCityId) {
      // GM and HOD can assign to PM
      return users.filter(u => {
        if (u.role !== 'pm') return false;
        const cityIds = normalizeCityIds((u as any).cityIds, (u as any).cityId);
        return cityIds.includes(filterCityId);
      });
    }
    if (isPM && filterCityId && currentUser?.id) {
      // PM can assign to other PMs from the same city (same as HOD selection)
      return users.filter(u => {
        if (u.role !== 'pm' || u.id === currentUser.id) return false;
        const cityIds = normalizeCityIds((u as any).cityIds, (u as any).cityId);
        return cityIds.includes(filterCityId);
      });
    }
    if (isAdmin && selectedCityId) {
      return users.filter(u => {
        if (u.role !== 'pm') return false;
        const cityIds = normalizeCityIds((u as any).cityIds, (u as any).cityId);
        return cityIds.includes(selectedCityId);
      });
    }
    return [];
  })();

  const availableHODs = (() => {
    if ((isGM || isPM) && filterCityId) {
      // GM and PM can assign to HOD
      return users.filter(u => {
        if (u.role !== 'hod' && u.role !== 'dpm') return false;
        const cityIds = normalizeCityIds((u as any).cityIds, (u as any).cityId);
        return cityIds.includes(filterCityId);
      });
    }
    if (isAdmin && selectedCityId) {
      return users.filter(u => {
        if (u.role !== 'hod' && u.role !== 'dpm') return false;
        const cityIds = normalizeCityIds((u as any).cityIds, (u as any).cityId);
        return cityIds.includes(selectedCityId);
      });
    }
    return [];
  })();

  const availableHODOnly = availableHODs.filter(u => u.role === 'hod');
  const availableDPMOnly = availableHODs.filter(u => u.role === 'dpm');

  const teamHasLeadUser = (t: any, userId: string) => {
    const h = Array.isArray(t.hodIds) ? t.hodIds : [];
    const d = Array.isArray(t.dpmIds) ? t.dpmIds : [];
    return (
      t.hodId === userId ||
      t.dpmId === userId ||
      h.includes(userId) ||
      d.includes(userId)
    );
  };

  const hodSlotUser = (hodSlotId: string) =>
    hodSlotId ? users.find(u => u.id === hodSlotId) : undefined;

  // Get available Staff (for PM and HOD)
  const availableStaff = (() => {
    if (isPM && currentUser?.id) {
      // PM can assign to Staff assigned to them
      return users.filter(
        u => u.role === 'staff' && (u as any).assignedByPmId === currentUser.id
      );
    }
    if (isHOD && currentUser?.id) {
      // HOD can assign to Staff assigned to them
      return users.filter(
        u => u.role === 'staff' && (u as any).assignedByHodId === currentUser.id
      );
    }
    return [];
  })();

  // Get available Users from the same city (for Admin and other roles)
  const getAvailableUsers = (cityId: string) => {
    if (!cityId) return [];
    return users.filter(u => {
      const userCityId = (u as any).cityId;
      const userCityIds = normalizeCityIds((u as any).cityIds, userCityId);
      return userCityIds.includes(cityId);
    });
  };

  // Get available teams for a specific task row based on its assignees
  const getAvailableTeamsForRow = (row: TaskRow) => {
    if (isAdmin && !selectedCityId) return [];
    const cityTeams = isAdmin
      ? teams.filter(t => t.cityId === selectedCityId)
      : teams;

    // Use row's assignee selections
    const rowGMId = row.selectedGMId;
    const rowPMId = row.selectedPMId;
    const rowHODId = row.selectedHODId;
    const rowStaffIds = row.selectedStaffIds;

    // Scenario 1: GM selected (with or without staff) — show GM's projects
    if (rowGMId && !rowPMId && !rowHODId) {
      return cityTeams.filter(t => t.gmId === rowGMId);
    }

    // Scenario 2: PM selected (with or without staff) — show PM's projects
    if (rowPMId && !rowGMId && !rowHODId) {
      return cityTeams.filter(t => {
        const pmIds = Array.isArray(t.pmIds) ? t.pmIds : [];
        return pmIds.includes(rowPMId) || t.pmId === rowPMId;
      });
    }

    // Scenario 3: HOD selected (with or without staff) — show HOD's projects
    if (rowHODId && !rowGMId && !rowPMId) {
      return cityTeams.filter(t => teamHasLeadUser(t, rowHODId));
    }

    // Scenario 4: Only staff selected (no GM/PM/HOD) — show projects by login user's city/role (HOD/PM/GM)
    if (rowStaffIds.length > 0 && !rowGMId && !rowPMId && !rowHODId) {
      return cityTeams;
    }

    // Scenario 5: GM + PM selected
    if (rowGMId && rowPMId && !rowHODId) {
      return cityTeams.filter(t => {
        const pmIds = Array.isArray(t.pmIds) ? t.pmIds : [];
        return pmIds.includes(rowPMId) || t.pmId === rowPMId;
      });
    }

    // Scenario 6: GM + HOD selected
    if (rowGMId && rowHODId && !rowPMId) {
      return cityTeams.filter(t => t.gmId === rowGMId);
    }

    // Scenario 7: PM + HOD selected
    if (rowPMId && rowHODId && !rowGMId) {
      return cityTeams.filter(t => {
        const pmIds = Array.isArray(t.pmIds) ? t.pmIds : [];
        return pmIds.includes(rowPMId) || t.pmId === rowPMId;
      });
    }

    // Scenario 8: All three selected
    if (rowGMId && rowPMId && rowHODId) {
      return cityTeams.filter(t => {
        const pmIds = Array.isArray(t.pmIds) ? t.pmIds : [];
        return pmIds.includes(rowPMId) || t.pmId === rowPMId;
      });
    }

    // If no roles selected, return all city teams (or empty if admin needs selection)
    return isAdmin ? [] : cityTeams;
  };

  // Get selected user IDs for a specific task row
  const getSelectedUserIdsForRow = (row: TaskRow): string[] => {
    const userIds: string[] = [];
    if (row.selectedGMId) userIds.push(row.selectedGMId);
    if (row.selectedPMId) userIds.push(row.selectedPMId);
    if (row.selectedHODId) userIds.push(row.selectedHODId);
    if (row.selectedStaffIds.length > 0) userIds.push(...row.selectedStaffIds);
    if (row.selectedUserIds.length > 0) userIds.push(...row.selectedUserIds);
    return userIds;
  };

  // Get available team projects based on selected roles and city
  // For GM/PM/HOD: show projects they're assigned to
  // For Admin: show projects based on selected assignees
  const availableTeams = (() => {
    // For GM/PM/HOD: show their own projects
    if (isGM && currentUser?.id) {
      return teams.filter(t => t.gmId === currentUser.id);
    }
    if (isPM && currentUser?.id) {
      return teams.filter(t => {
        const pmIds = Array.isArray(t.pmIds) ? t.pmIds : [];
        return pmIds.includes(currentUser.id) || t.pmId === currentUser.id;
      });
    }
    if (isHOD && currentUser?.id) {
      return teams.filter(t => teamHasLeadUser(t, currentUser.id));
    }

    // For Admin: use existing logic based on selected assignees
    if (isAdmin && !selectedCityId) return [];

    // Filter teams by city first (for admin)
    const cityTeams = isAdmin
      ? teams.filter(t => t.cityId === selectedCityId)
      : teams;

    // Scenario 1: GM selected (with or without staff) → show teams allocated to GM
    if (selectedGMId && !selectedPMId && !selectedHODId) {
      return cityTeams.filter(t => t.gmId === selectedGMId);
    }

    // Scenario 2: PM selected (with or without staff) → show teams allocated to PM
    if (selectedPMId && !selectedGMId && !selectedHODId) {
      return cityTeams.filter(t => {
        const pmIds = Array.isArray(t.pmIds) ? t.pmIds : [];
        return pmIds.includes(selectedPMId) || t.pmId === selectedPMId;
      });
    }

    // Scenario 3: HOD selected (with or without staff) → show teams allocated to HOD
    if (selectedHODId && !selectedGMId && !selectedPMId) {
      return cityTeams.filter(t => teamHasLeadUser(t, selectedHODId));
    }

    // Scenario 4: Only staff selected (no GM/PM/HOD) → show projects by login user's city (admin: selected city)
    if (
      selectedStaffIds.length > 0 &&
      !selectedGMId &&
      !selectedPMId &&
      !selectedHODId
    ) {
      return cityTeams;
    }

    // Scenario 5: GM + PM selected → show teams allocated to PM (PM priority)
    if (selectedGMId && selectedPMId && !selectedHODId) {
      return cityTeams.filter(t => {
        const pmIds = Array.isArray(t.pmIds) ? t.pmIds : [];
        return pmIds.includes(selectedPMId) || t.pmId === selectedPMId;
      });
    }

    // Scenario 6: GM + HOD selected → show teams allocated to GM (GM priority)
    if (selectedGMId && selectedHODId && !selectedPMId) {
      return cityTeams.filter(t => t.gmId === selectedGMId);
    }

    // Scenario 7: PM + HOD selected → show teams allocated to PM (PM priority)
    if (selectedPMId && selectedHODId && !selectedGMId) {
      return cityTeams.filter(t => {
        const pmIds = Array.isArray(t.pmIds) ? t.pmIds : [];
        return pmIds.includes(selectedPMId) || t.pmId === selectedPMId;
      });
    }

    // Scenario 8: All three selected → show teams allocated to PM (PM priority)
    if (selectedGMId && selectedPMId && selectedHODId) {
      return cityTeams.filter(t => {
        const pmIds = Array.isArray(t.pmIds) ? t.pmIds : [];
        return pmIds.includes(selectedPMId) || t.pmId === selectedPMId;
      });
    }

    // If no roles selected, return empty array
    return [];
  })();

  // When city changes (for admin), reset GM/PM/HOD selections, but preserve task rows structure
  useEffect(() => {
    if (isAdmin && selectedCityId) {
      setSelectedGMId('');
      setSelectedPMId('');
      setSelectedHODId('');
      setSelectedStaffIds([]);
      // Keep task rows structure but clear teamId selections since teams are city-specific
      setTaskRows(prev => {
        if (prev.length === 0) {
          return [
            {
              id: `row-${Date.now()}`,
              teamId: '',
              title: '',
              description: '',
              dueDate: initialDueDate || '',
              selectedGMId: '',
              selectedPMId: '',
              selectedHODId: '',
              selectedStaffIds: [],
              selectedUserIds: [],
              taskType: 'common',
            },
          ];
        }
        // Clear teamId but keep other fields (including taskType)
        return prev.map(r => ({
          ...r,
          teamId: '',
          selectedGMId: '',
          selectedPMId: '',
          selectedHODId: '',
          selectedStaffIds: [],
          selectedUserIds: [],
        }));
      });
    }
  }, [selectedCityId, initialDueDate, isAdmin]);

  const addTaskRow = (
    preselectedTeamId?: string,
    templateRow?: Partial<TaskRow>
  ) => {
    setTaskRows([
      ...taskRows,
      {
        id: `row-${Date.now()}-${Math.random()}`,
        teamId: preselectedTeamId || templateRow?.teamId || '',
        title: templateRow?.title || '',
        description: templateRow?.description || '',
        dueDate: templateRow?.dueDate || initialDueDate || '',
        selectedGMId: templateRow?.selectedGMId || '',
        selectedPMId: templateRow?.selectedPMId || '',
        selectedHODId: templateRow?.selectedHODId || '',
        selectedStaffIds: templateRow?.selectedStaffIds
          ? [...templateRow.selectedStaffIds]
          : [],
        selectedUserIds: templateRow?.selectedUserIds
          ? [...templateRow.selectedUserIds]
          : [],
        taskType: templateRow?.taskType || 'common',
      },
    ]);
  };

  const addTaskRowSameProject = () => {
    // Find the most recent task row with a project selected
    const lastRowWithProject = [...taskRows].reverse().find(row => row.teamId);
    if (lastRowWithProject) {
      // Copy both project and assignees from the last row
      addTaskRow(lastRowWithProject.teamId, {
        teamId: lastRowWithProject.teamId,
        selectedGMId: lastRowWithProject.selectedGMId,
        selectedPMId: lastRowWithProject.selectedPMId,
        selectedHODId: lastRowWithProject.selectedHODId,
        selectedStaffIds: lastRowWithProject.selectedStaffIds,
        selectedUserIds: lastRowWithProject.selectedUserIds,
      });
    } else {
      addTaskRow();
    }
  };

  const addTaskRowDifferentProject = () => {
    addTaskRow(); // No preselected project
  };

  const removeTaskRow = (rowId: string) => {
    if (taskRows.length > 1) {
      setTaskRows(taskRows.filter(row => row.id !== rowId));
    } else {
      // If last row, just clear it
      setTaskRows([
        {
          id: `row-${Date.now()}`,
          teamId: '',
          title: '',
          description: '',
          dueDate: initialDueDate || '',
          selectedGMId: '',
          selectedPMId: '',
          selectedHODId: '',
          selectedStaffIds: [],
          selectedUserIds: [],
          taskType: 'common',
        },
      ]);
    }
  };

  const updateTaskRow = (
    rowId: string,
    field: keyof TaskRow,
    value: string | string[]
  ) => {
    setTaskRows(
      taskRows.map(row => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const saveTaskRowToLocalStorage = (row: TaskRow) => {
    // Validate required fields
    if (!row.teamId || !row.title || !row.dueDate) {
      toast({
        title: 'Cannot Save',
        description:
          'Please fill in required fields (Project, Title, and Due Date)',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Saved',
      description: 'Task row has been added.',
    });
  };

  const hasUnsavedData = () => {
    return (
      (isAdmin && selectedCityId !== '') ||
      taskRows.some(
        row =>
          row.teamId ||
          row.title ||
          row.description ||
          row.dueDate ||
          row.selectedGMId ||
          row.selectedPMId ||
          row.selectedHODId ||
          row.selectedStaffIds.length > 0 ||
          row.selectedUserIds.length > 0 ||
          row.taskType !== 'common'
      )
    );
  };

  // Get the assigned user ID based on selections (priority: GM > PM > HOD > Staff)
  // If multiple are selected, use GM first, then PM, then HOD, then first Staff
  const getAssignedUserId = (): string | null => {
    if (selectedGMId) return selectedGMId;
    if (selectedPMId) return selectedPMId;
    if (selectedHODId) return selectedHODId;
    if (selectedStaffIds.length > 0) return selectedStaffIds[0] || null;
    return null;
  };

  // Get all selected user IDs (for creating multiple tasks)
  const getSelectedUserIds = (): string[] => {
    const userIds: string[] = [];
    if (selectedGMId) userIds.push(selectedGMId);
    if (selectedPMId) userIds.push(selectedPMId);
    if (selectedHODId) userIds.push(selectedHODId);
    if (selectedStaffIds.length > 0) userIds.push(...selectedStaffIds);
    return userIds;
  };

  // Check if at least one role is selected
  const hasSelectedRole = (): boolean => {
    return (
      selectedGMId !== '' ||
      selectedPMId !== '' ||
      selectedHODId !== '' ||
      selectedStaffIds.length > 0
    );
  };

  const createTaskMutation = useMutation({
    mutationFn: async () => {
      // Validate all rows
      const validRows = taskRows.filter(row => {
        const hasAssignee = getSelectedUserIdsForRow(row).length > 0;
        return row.teamId && row.title && row.dueDate && hasAssignee;
      });

      if (validRows.length === 0) {
        throw new Error(
          'Please fill at least one complete task row with assignee selected'
        );
      }

      // Create all tasks in bulk for efficient email batching
      const tasksToCreate: any[] = [];

      for (const row of validRows) {
        const rowAssigneeIds = getSelectedUserIdsForRow(row);
        if (rowAssigneeIds.length === 0) continue;

        if (row.taskType === 'individual') {
          // Existing behavior: one task per user
          for (const userId of rowAssigneeIds) {
            tasksToCreate.push({
              title: row.title,
              description: row.description || null,
              teamId: row.teamId,
              projectId: null,
              assignedTo: userId,
              dueDate: new Date(row.dueDate).toISOString(),
              status: 'pending',
              isRecurring: false,
              recurrenceFrequency: null,
              recurrenceEndDate: null,
              isCommon: false,
            });
          }
        } else {
          // Common task: a single shared task row visible to all selected users
          const primaryAssignee = rowAssigneeIds[0];
          tasksToCreate.push({
            title: row.title,
            description: row.description || null,
            teamId: row.teamId,
            projectId: null,
            assignedTo: primaryAssignee,
            dueDate: new Date(row.dueDate).toISOString(),
            status: 'pending',
            isRecurring: false,
            recurrenceFrequency: null,
            recurrenceEndDate: null,
            isCommon: true,
            assigneeIds: rowAssigneeIds,
          });
        }
      }

      // Create all tasks in one bulk request
      const bulkResponse = await apiRequest('POST', '/api/tasks/bulk', {
        tasks: tasksToCreate,
      });
      const bulkData = await bulkResponse.json();
      const createdTaskIds = bulkData.tasks.map((t: any) => t.id);

      // If GM is selected as viewer, add GM as viewer to all created tasks
      if (addGMAsViewer) {
        // Use selected GM if available, otherwise use the first available GM in the city
        const gmToAddAsViewer =
          selectedGMId ||
          (availableGMs.length > 0 && availableGMs[0]
            ? availableGMs[0].id
            : null);

        if (gmToAddAsViewer) {
          for (const taskId of createdTaskIds) {
            try {
              await apiRequest('POST', `/api/tasks/${taskId}/viewers`, {
                userId: gmToAddAsViewer,
              });
            } catch (error) {
              console.error('Error adding GM as viewer:', error);
              // Don't fail the entire operation if viewer addition fails
            }
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/analytics/task-stats'],
      });
      queryClient.invalidateQueries({
        predicate: query =>
          Array.isArray(query.queryKey) && query.queryKey[0] === '/api/tasks',
      });
      clearSavedData(); // Clear saved data on success
      onClose();
      // Calculate total tasks created from the mutation
      const validRows = taskRows.filter(row => {
        const hasAssignee = getSelectedUserIdsForRow(row).length > 0;
        return row.teamId && row.title && row.dueDate && hasAssignee;
      });
      const totalTasksCreated = validRows.reduce((sum, row) => {
        return (
          sum +
          (row.taskType === 'individual'
            ? getSelectedUserIdsForRow(row).length
            : 1)
        );
      }, 0);
      toast({
        title: 'Success',
        description: `Successfully created ${totalTasksCreated} task(s)`,
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to create tasks',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = () => {
    // Validate city selection
    if (!selectedCityId) {
      toast({
        title: 'Error',
        description: 'Please select a city',
        variant: 'destructive',
      });
      return;
    }

    // Validate at least one complete row with assignee
    const validRows = taskRows.filter(row => {
      const hasAssignee = getSelectedUserIdsForRow(row).length > 0;
      return row.teamId && row.title && row.dueDate && hasAssignee;
    });

    if (validRows.length === 0) {
      toast({
        title: 'Error',
        description:
          'Please fill at least one complete task row with assignee selected (Project, Title, Due Date, and Assignee are required)',
        variant: 'destructive',
      });
      return;
    }

    createTaskMutation.mutate();
  };

  const handleClose = () => {
    if (createTaskMutation.isPending) return;

    if (hasUnsavedData()) {
      setShowConfirmCloseDialog(true);
    } else {
      clearSavedData();
      onClose();
    }
  };

  const handleConfirmClose = () => {
    clearSavedData();
    setShowConfirmCloseDialog(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmCloseDialog(false);
  };

  const handleCloseButton = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  // For edit mode, use the old simple form
  if (editTask) {
    return (
      <>
        <Dialog open={isOpen} onOpenChange={handleCloseButton}>
          <DialogContent className="w-full max-w-lg md:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Task</DialogTitle>
              <DialogDescription>
                Update the task details below
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Task Title</Label>
                <Input
                  defaultValue={editTask.title}
                  placeholder="Enter task title"
                  readOnly
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  defaultValue={editTask.description || ''}
                  placeholder="Task description..."
                  rows={3}
                  readOnly
                />
              </div>
              <p className="text-sm text-gray-500">
                Task editing is not available in this view. Please use the task
                board to edit tasks.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleCloseButton(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmation Dialog for Unsaved Changes */}
        <AlertDialog
          open={showConfirmCloseDialog}
          onOpenChange={setShowConfirmCloseDialog}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved data. Are you sure you want to close? All
                unsaved changes will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancelClose}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmClose}
                className="bg-red-600 hover:bg-red-700"
              >
                Close Without Saving
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleCloseButton}>
        <DialogContent className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
            <DialogDescription>
              {isAdmin
                ? 'Select city, assign to GM/PM/HOD/DPM, and create multiple tasks at once'
                : isGM
                  ? 'Assign tasks to PM, HOD, or DPM in your city'
                  : isPM
                    ? 'Assign tasks to Staff (assigned to you), HOD, DPM, or PM (same city)'
                    : isHOD
                      ? 'Assign tasks to GM, PM, or Staff (assigned to you)'
                      : 'Create multiple tasks at once'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Step 1: City Selection */}
            {/* Admin must pick a city. PM/HOD with multiple cities can pick a city to filter assignees. */}
            {shouldPickCity && (
              <div>
                <Label htmlFor="city-select">
                  City <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={selectedCityId}
                  onValueChange={setSelectedCityId}
                >
                  <SelectTrigger id="city-select" className="w-full">
                    <SelectValue placeholder="Select a city" />
                  </SelectTrigger>
                  <SelectContent>
                    {(isAdmin
                      ? cities
                      : cities.filter(c => currentUserCityIds.includes(c.id))
                    )
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(city => (
                        <SelectItem key={city.id} value={city.id}>
                          {city.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {isAdmin && cities.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No cities available. Create a city first.
                  </p>
                )}
                {!isAdmin &&
                  currentUserCityIds.length > 1 &&
                  !selectedCityId && (
                    <p className="text-xs text-amber-600 mt-1">
                      Select a city to see PM/HOD/DPM options.
                    </p>
                  )}
              </div>
            )}

            {/* Task Rows */}
            {(isAdmin
              ? selectedCityId
              : filterCityId || currentUserCityIds.length > 0) && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Tasks</Label>
                </div>

                {taskRows.map((row, index) => {
                  const rowAvailableTeams = getAvailableTeamsForRow(row);

                  return (
                    <div
                      key={row.id}
                      className="border rounded-lg p-4 space-y-3 bg-gray-50"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium">
                          Task {index + 1}
                        </Label>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTaskRow(row.id)}
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                            title="Delete this task row"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Task Type Selection - Per Task */}
                      <div>
                        <Label className="mb-2 block">
                          Task Type <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`task-type-${row.id}`}
                              value="common"
                              checked={row.taskType === 'common'}
                              onChange={e =>
                                updateTaskRow(
                                  row.id,
                                  'taskType',
                                  e.target.value as 'individual' | 'common'
                                )
                              }
                              className="w-4 h-4 text-blue-600 cursor-pointer"
                            />
                            <span className="text-sm font-medium">
                              Common Task (shared)
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`task-type-${row.id}`}
                              value="individual"
                              checked={row.taskType === 'individual'}
                              onChange={e =>
                                updateTaskRow(
                                  row.id,
                                  'taskType',
                                  e.target.value as 'individual' | 'common'
                                )
                              }
                              className="w-4 h-4 text-blue-600 cursor-pointer"
                            />
                            <span className="text-sm font-medium">
                              Individual Task
                            </span>
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {row.taskType === 'individual'
                            ? 'Each selected user will receive their own separate task.'
                            : 'All selected users will share a single common task with shared status and progress.'}
                        </p>
                      </div>

                      {/* Assign Task To - Per Task */}
                      <div>
                        <Label className="mb-3 block">
                          Assign Task To <span className="text-red-500">*</span>
                        </Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                          {/* GM Selection - Show for Admin and HOD */}
                          {(isAdmin || isHOD) && availableGMs.length > 0 && (
                            <div>
                              <Label
                                htmlFor={`gm-select-${row.id}`}
                                className="text-sm"
                              >
                                General Manager (GM)
                              </Label>
                              <Popover
                                open={openGmRowId === row.id}
                                onOpenChange={open =>
                                  setOpenGmRowId(open ? row.id : null)
                                }
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={gmOpen}
                                    className="w-full justify-between"
                                    id={`gm-select-${row.id}`}
                                  >
                                    {row.selectedGMId
                                      ? availableGMs.find(
                                          gm => gm.id === row.selectedGMId
                                        )
                                        ? `${availableGMs.find(gm => gm.id === row.selectedGMId)?.firstName} ${availableGMs.find(gm => gm.id === row.selectedGMId)?.lastName}`
                                        : 'Select GM'
                                      : 'Select GM'}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-[var(--radix-popover-trigger-width)] p-0"
                                  align="start"
                                >
                                  <Command>
                                    <CommandInput placeholder="Search GM..." />
                                    <CommandList>
                                      <CommandEmpty>No GM found.</CommandEmpty>
                                      <CommandGroup>
                                        <CommandItem
                                          value="none"
                                          onSelect={() => {
                                            updateTaskRow(
                                              row.id,
                                              'selectedGMId',
                                              ''
                                            );
                                            setOpenGmRowId(null);
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              'mr-2 h-4 w-4',
                                              row.selectedGMId === ''
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                            )}
                                          />
                                          Select GM
                                        </CommandItem>
                                        {availableGMs.map(gm => (
                                          <CommandItem
                                            key={gm.id}
                                            value={`${gm.firstName} ${gm.lastName}`}
                                            onSelect={() => {
                                              updateTaskRow(
                                                row.id,
                                                'selectedGMId',
                                                row.selectedGMId === gm.id
                                                  ? ''
                                                  : gm.id
                                              );
                                              setOpenGmRowId(null);
                                            }}
                                          >
                                            <Check
                                              className={cn(
                                                'mr-2 h-4 w-4',
                                                row.selectedGMId === gm.id
                                                  ? 'opacity-100'
                                                  : 'opacity-0'
                                              )}
                                            />
                                            {gm.firstName} {gm.lastName}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}

                          {/* PM Selection - Show for Admin, GM, HOD, and PM (PM can assign to other PMs in same city) */}
                          {(isAdmin || isGM || isHOD || isPM) && (
                            <div>
                              <Label
                                htmlFor={`pm-select-${row.id}`}
                                className="text-sm"
                              >
                                Project Manager (PM)
                              </Label>
                              <Popover
                                open={openPmRowId === row.id}
                                onOpenChange={open =>
                                  setOpenPmRowId(open ? row.id : null)
                                }
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={openPmRowId === row.id}
                                    className="w-full justify-between"
                                    id={`pm-select-${row.id}`}
                                  >
                                    {row.selectedPMId
                                      ? availablePMs.find(
                                          pm => pm.id === row.selectedPMId
                                        )
                                        ? `${availablePMs.find(pm => pm.id === row.selectedPMId)?.firstName} ${availablePMs.find(pm => pm.id === row.selectedPMId)?.lastName}`
                                        : 'Select PM'
                                      : 'Select PM'}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-full p-0"
                                  align="start"
                                >
                                  <Command>
                                    <CommandInput placeholder="Search PM..." />
                                    <CommandList>
                                      <CommandEmpty>
                                        {availablePMs.length === 0
                                          ? 'No PM available'
                                          : 'No PM found.'}
                                      </CommandEmpty>
                                      <CommandGroup>
                                        {availablePMs.length > 0
                                          ? availablePMs.map(pm => (
                                              <CommandItem
                                                key={pm.id}
                                                value={`${pm.firstName} ${pm.lastName}`}
                                                onSelect={() => {
                                                  updateTaskRow(
                                                    row.id,
                                                    'selectedPMId',
                                                    row.selectedPMId === pm.id
                                                      ? ''
                                                      : pm.id
                                                  );
                                                  setOpenPmRowId(null);
                                                }}
                                              >
                                                <Check
                                                  className={cn(
                                                    'mr-2 h-4 w-4',
                                                    row.selectedPMId === pm.id
                                                      ? 'opacity-100'
                                                      : 'opacity-0'
                                                  )}
                                                />
                                                {pm.firstName} {pm.lastName}
                                              </CommandItem>
                                            ))
                                          : null}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}

                          {/* HOD Selection - Show for Admin, GM, and PM */}
                          {(isAdmin || isGM || isPM) && (
                            <div>
                              <Label
                                htmlFor={`hod-select-${row.id}`}
                                className="text-sm"
                              >
                                Head of Department (HOD)
                              </Label>
                              <Popover
                                open={openHodRowId === row.id}
                                onOpenChange={open => {
                                  setOpenHodRowId(open ? row.id : null);
                                  if (open) setOpenDpmRowId(null);
                                }}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={openHodRowId === row.id}
                                    className="w-full justify-between"
                                    id={`hod-select-${row.id}`}
                                  >
                                    {(() => {
                                      const u = hodSlotUser(row.selectedHODId);
                                      if (
                                        u &&
                                        u.role === 'hod' &&
                                        row.selectedHODId
                                      ) {
                                        return `${u.firstName} ${u.lastName}`;
                                      }
                                      return 'Select HOD';
                                    })()}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-full p-0"
                                  align="start"
                                >
                                  <Command>
                                    <CommandInput placeholder="Search HOD..." />
                                    <CommandList>
                                      <CommandEmpty>
                                        {availableHODOnly.length === 0
                                          ? 'No HOD available'
                                          : 'No HOD found.'}
                                      </CommandEmpty>
                                      <CommandGroup>
                                        {availableHODOnly.length > 0
                                          ? availableHODOnly.map(hod => (
                                              <CommandItem
                                                key={hod.id}
                                                value={`${hod.firstName} ${hod.lastName}`}
                                                onSelect={() => {
                                                  updateTaskRow(
                                                    row.id,
                                                    'selectedHODId',
                                                    row.selectedHODId === hod.id
                                                      ? ''
                                                      : hod.id
                                                  );
                                                  setOpenHodRowId(null);
                                                }}
                                              >
                                                <Check
                                                  className={cn(
                                                    'mr-2 h-4 w-4',
                                                    row.selectedHODId === hod.id
                                                      ? 'opacity-100'
                                                      : 'opacity-0'
                                                  )}
                                                />
                                                {hod.firstName} {hod.lastName}
                                              </CommandItem>
                                            ))
                                          : null}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}

                          {/* DPM Selection — team stores HOD in hod_ids and DPM in dpm_ids */}
                          {(isAdmin || isGM || isPM) && (
                            <div>
                              <Label
                                htmlFor={`dpm-select-${row.id}`}
                                className="text-sm"
                              >
                                DPM
                              </Label>
                              <Popover
                                open={openDpmRowId === row.id}
                                onOpenChange={open => {
                                  setOpenDpmRowId(open ? row.id : null);
                                  if (open) setOpenHodRowId(null);
                                }}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={openDpmRowId === row.id}
                                    className="w-full justify-between"
                                    id={`dpm-select-${row.id}`}
                                  >
                                    {(() => {
                                      const u = hodSlotUser(row.selectedHODId);
                                      if (
                                        u &&
                                        u.role === 'dpm' &&
                                        row.selectedHODId
                                      ) {
                                        return `${u.firstName} ${u.lastName}`;
                                      }
                                      return 'Select DPM';
                                    })()}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-full p-0"
                                  align="start"
                                >
                                  <Command>
                                    <CommandInput placeholder="Search DPM..." />
                                    <CommandList>
                                      <CommandEmpty>
                                        {availableDPMOnly.length === 0
                                          ? 'No DPM available'
                                          : 'No DPM found.'}
                                      </CommandEmpty>
                                      <CommandGroup>
                                        {availableDPMOnly.length > 0
                                          ? availableDPMOnly.map(dpm => (
                                              <CommandItem
                                                key={dpm.id}
                                                value={`${dpm.firstName} ${dpm.lastName}`}
                                                onSelect={() => {
                                                  updateTaskRow(
                                                    row.id,
                                                    'selectedHODId',
                                                    row.selectedHODId === dpm.id
                                                      ? ''
                                                      : dpm.id
                                                  );
                                                  setOpenDpmRowId(null);
                                                }}
                                              >
                                                <Check
                                                  className={cn(
                                                    'mr-2 h-4 w-4',
                                                    row.selectedHODId === dpm.id
                                                      ? 'opacity-100'
                                                      : 'opacity-0'
                                                  )}
                                                />
                                                {dpm.firstName} {dpm.lastName}
                                              </CommandItem>
                                            ))
                                          : null}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}

                          {/* Staff Selection - Show for PM and HOD */}
                          {(isPM || isHOD) && availableStaff.length > 0 && (
                            <div>
                              <Label
                                htmlFor={`staff-select-${row.id}`}
                                className="text-sm"
                              >
                                Staff{' '}
                                {row.selectedStaffIds.length > 0 &&
                                  `(${row.selectedStaffIds.length} selected)`}
                              </Label>
                              <Popover
                                open={openStaffRowId === row.id}
                                onOpenChange={open =>
                                  setOpenStaffRowId(open ? row.id : null)
                                }
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={openStaffRowId === row.id}
                                    className="w-full justify-between"
                                    id={`staff-select-${row.id}`}
                                  >
                                    {row.selectedStaffIds.length > 0
                                      ? `${row.selectedStaffIds.length} Staff selected`
                                      : 'Select Staff'}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-full p-0"
                                  align="start"
                                >
                                  <Command>
                                    <CommandInput placeholder="Search Staff..." />
                                    <CommandList>
                                      <CommandEmpty>
                                        {availableStaff.length === 0
                                          ? 'No Staff available'
                                          : 'No Staff found.'}
                                      </CommandEmpty>
                                      <CommandGroup>
                                        {availableStaff.map(staff => (
                                          <CommandItem
                                            key={staff.id}
                                            value={`${staff.firstName} ${staff.lastName}`}
                                            onSelect={() => {
                                              const newStaffIds =
                                                row.selectedStaffIds.includes(
                                                  staff.id
                                                )
                                                  ? row.selectedStaffIds.filter(
                                                      id => id !== staff.id
                                                    )
                                                  : [
                                                      ...row.selectedStaffIds,
                                                      staff.id,
                                                    ];
                                              updateTaskRow(
                                                row.id,
                                                'selectedStaffIds',
                                                newStaffIds
                                              );
                                              setOpenStaffRowId(null);
                                            }}
                                          >
                                            <Check
                                              className={cn(
                                                'mr-2 h-4 w-4',
                                                row.selectedStaffIds.includes(
                                                  staff.id
                                                )
                                                  ? 'opacity-100'
                                                  : 'opacity-0'
                                              )}
                                            />
                                            {staff.firstName} {staff.lastName}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}
                        </div>
                        {getSelectedUserIdsForRow(row).length === 0 && (
                          <p className="text-xs text-amber-600 mt-2">
                            Please select at least one person to assign this
                            task to.
                          </p>
                        )}
                      </div>

                      <div>
                        <Label>
                          Projects<span className="text-red-500">*</span>
                        </Label>
                        <Popover
                          open={openProjectRowId === row.id}
                          onOpenChange={open =>
                            setOpenProjectRowId(open ? row.id : null)
                          }
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={openProjectRowId === row.id}
                              className="w-full justify-between"
                            >
                              {row.teamId
                                ? rowAvailableTeams.find(
                                    team => team.id === row.teamId
                                  )?.name || 'Select Project Name'
                                : 'Select Project Name'}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-[var(--radix-popover-trigger-width)] p-0"
                            align="start"
                          >
                            <Command>
                              <CommandInput placeholder="Search project..." />
                              <CommandList>
                                <CommandEmpty>
                                  {rowAvailableTeams.length === 0
                                    ? 'No projects available. Select assignees first or create projects.'
                                    : 'No project found.'}
                                </CommandEmpty>
                                <CommandGroup>
                                  {rowAvailableTeams.length > 0 &&
                                    rowAvailableTeams
                                      .sort((a, b) =>
                                        a.name.localeCompare(b.name)
                                      )
                                      .map(team => (
                                        <CommandItem
                                          key={team.id}
                                          value={team.name}
                                          onSelect={() => {
                                            updateTaskRow(
                                              row.id,
                                              'teamId',
                                              team.id
                                            );
                                            setOpenProjectRowId(null);
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              'mr-2 h-4 w-4',
                                              row.teamId === team.id
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                            )}
                                          />
                                          {team.name}
                                        </CommandItem>
                                      ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {rowAvailableTeams.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">
                            {isAdmin
                              ? 'No projects available for selected assignees. Select different assignees or create projects first.'
                              : 'No projects assigned to you. Contact admin to assign projects.'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label>
                          Title <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={row.title}
                          onChange={e =>
                            updateTaskRow(row.id, 'title', e.target.value)
                          }
                          placeholder="Enter task title"
                        />
                      </div>

                      <div>
                        <Label>Description (Optional)</Label>
                        <Textarea
                          value={row.description}
                          onChange={e =>
                            updateTaskRow(row.id, 'description', e.target.value)
                          }
                          placeholder="Task description..."
                          rows={2}
                        />
                      </div>

                      <div>
                        <Label>
                          Due Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="date"
                          value={row.dueDate}
                          onChange={e =>
                            updateTaskRow(row.id, 'dueDate', e.target.value)
                          }
                        />
                      </div>
                    </div>
                  );
                })}

                {/* Add New Task buttons below the list */}
                <div className="flex justify-center pt-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTaskRowSameProject}
                    disabled={!taskRows.some(row => row.teamId)}
                    className="flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add new task for same project
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTaskRowDifferentProject}
                    className="flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add new task for different project
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="sticky bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-900/95 border-t border-gray-200 dark:border-gray-800 pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createTaskMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={
                createTaskMutation.isPending ||
                !selectedCityId ||
                taskRows.filter(row => {
                  const hasAssignee = getSelectedUserIdsForRow(row).length > 0;
                  return row.teamId && row.title && row.dueDate && hasAssignee;
                }).length === 0
              }
            >
              {createTaskMutation.isPending
                ? 'Creating...'
                : (() => {
                    const validRows = taskRows.filter(row => {
                      const hasAssignee =
                        getSelectedUserIdsForRow(row).length > 0;
                      return (
                        row.teamId && row.title && row.dueDate && hasAssignee
                      );
                    });
                    const totalTasks = validRows.reduce((sum, row) => {
                      return (
                        sum +
                        (row.taskType === 'individual'
                          ? getSelectedUserIdsForRow(row).length
                          : 1)
                      );
                    }, 0);
                    if (totalTasks > 0) {
                      return `Create ${totalTasks} Task(s)`;
                    }
                    return 'Create Task(s)';
                  })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Unsaved Changes */}
      <AlertDialog
        open={showConfirmCloseDialog}
        onOpenChange={setShowConfirmCloseDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved data. Are you sure you want to close? All unsaved
              changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmClose}
              className="bg-red-600 hover:bg-red-700"
            >
              Close Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
